export const objectKeys = (item: any): string[] => (item ? Object.keys(item) : [])

export const safeObject = (obj: object) => ({
  access: (keyAccess) =>
    keyAccess.split('.').reduce((nestedObject: object, key: string) => {
      if (nestedObject && key in nestedObject) {
        return nestedObject[key]
      }
      return undefined
    }, obj as any)
})

export const safeObjectData = (data: any, key: string | string[], defaultReturn = {} as any) =>
  safeObject(data).access(Array.isArray(key) ? key.join('.') : key) || defaultReturn

export const toDictionary = (arr, key = 'id', isArrayMode = false) =>
  arr.reduce((obj, item) => ({ ...obj, [item[key]]: !isArrayMode ? item : [...(obj[item[key]] || []), item] }), {})

export const deepEqual = (x, y) => {
  if (x === y) {
    return true
  } else if ((x instanceof RegExp && y instanceof RegExp) || (x instanceof Function && y instanceof Function)) {
    return x.toString() === y.toString()
  } else if (typeof x === 'object' && x != null && typeof y === 'object' && y != null) {
    const [xKeys, yKeys] = [objectKeys(x), objectKeys(y)]

    if (xKeys.length !== yKeys.length) {
      return false
    }

    return Array.from(new Set([...xKeys, ...yKeys])).every((key) => deepEqual(x[key], y[key]))
  } else {
    return false
  }
}

type ValueOf<T> = T[keyof T]

export const getEnumKeyByEnumValue = <R extends string | number, T extends { [key: string]: R }>(
  myEnum: T,
  enumValue: ValueOf<T>
): string => {
  const keys = objectKeys(myEnum).filter((x) => myEnum[x] === enumValue)
  return keys.length > 0 ? keys[0] : ''
}

export const exists = (item: unknown): boolean =>
  item !== undefined && item !== null && !!Object.keys(item as object).length

/**
 * Plain nullish check. Unlike `exists`, it does not require the value to be a non-empty object, so
 * it also holds for primitives such as `false` or `0`.
 */
export const isNotNullish = (value: unknown): boolean => value !== undefined && value !== null

export const distinctItems = (arr: any[]): any[] => Array.from(new Set(arr))

const toLowerCaseNoAccent = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(
      /([àáâãäå])|([çčć])|([èéêë])|([ìíîï])|([ñ])|([òóôõöø])|([ß])|([ùúûü])|([ÿ])|([æ])/g,
      (_str, a, c, e, i, n, o, s, u, y, ae) =>
        (a && 'a') ||
        (c && 'c') ||
        (e && 'e') ||
        (i && 'i') ||
        (n && 'n') ||
        (o && 'o') ||
        (s && 's') ||
        (u && 'u') ||
        (y && 'y') ||
        (ae && 'ae')
    )

export const filterByTerm = (items: any[], keys: string, term: string) => {
  if (!term || !keys) {
    return items
  }

  return (items || []).filter((item) =>
    keys.split(',').some((key) => {
      const keyValue = key.trim()
      return (
        Object.prototype.hasOwnProperty.call(item, keyValue) &&
        new RegExp(toLowerCaseNoAccent(term).trim(), 'gi').test(
          toLowerCaseNoAccent(String(!!item[keyValue] || item[keyValue] === 0 ? item[keyValue] : '')).trim()
        )
      )
    })
  )
}
